import { Close, Content, Description, Overlay, Portal, Root, Title, Trigger } from '@radix-ui/react-dialog';
import { type ComponentPropsWithoutRef, type ElementRef, type HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/cn';
import Icon from '../Icon/Icon';

/**
 * イベント伝播を防ぐハンドラーを生成する高階関数
 * ダイアログ内のクリックが背後の要素（テーブル行など）に伝播するのを防ぐために使用
 */
const createStopPropagationHandler = <T extends React.MouseEvent<HTMLElement>,>(originalHandler?: (e: T) => void) => {
  return (e: T) => {
    e.stopPropagation();
    originalHandler?.(e);
  };
};
const Dialog = Root;
const DialogTrigger = Trigger;
const DialogPortal = Portal;
const DialogClose = Close;
const DialogOverlay = forwardRef<ElementRef<typeof Overlay>, ComponentPropsWithoutRef<typeof Overlay>>(({
  className,
  onClick,
  onMouseDown,
  ...props
}, ref) => <Overlay ref={ref} className={cn('fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', className)} onClick={createStopPropagationHandler(onClick)} onMouseDown={createStopPropagationHandler(onMouseDown)} {...props} />);
DialogOverlay.displayName = Overlay.displayName;
const DialogContent = forwardRef<ElementRef<typeof Content>, ComponentPropsWithoutRef<typeof Content> & {
  /**
   * ダイアログの閉じるボタンを非表示にする
   * @default false
   */
  hideCloseButton?: boolean;
}>(({
  hideCloseButton = false,
  className,
  children,
  onClick,
  onMouseDown,
  ...props
}, ref) => <DialogPortal>
      <DialogOverlay />
      <Content ref={ref} className={cn('fixed left-[50%] top-[50%] z-50 grid max-h-[calc(100vh-64px)] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-lg', className)} onClick={createStopPropagationHandler(onClick)} onMouseDown={createStopPropagationHandler(onMouseDown)} {...props}>
        {children}
        {!hideCloseButton && <Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-primary-muted-fill data-[state=open]:text-muted-foreground">
            <Icon iconName="XIcon" />
            <span className="sr-only">Close</span>
          </Close>}
      </Content>
    </DialogPortal>);
DialogContent.displayName = Content.displayName;
const DialogHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />;
DialogHeader.displayName = 'DialogHeader';
const DialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />;
DialogFooter.displayName = 'DialogFooter';
const DialogTitle = forwardRef<ElementRef<typeof Title>, ComponentPropsWithoutRef<typeof Title>>(({
  className,
  ...props
}, ref) => <Title ref={ref} className={cn('text-lg font-semibold leading-none tracking-tight', className)} {...props} />);
DialogTitle.displayName = Title.displayName;
const DialogDescription = forwardRef<ElementRef<typeof Description>, ComponentPropsWithoutRef<typeof Description>>(({
  className,
  ...props
}, ref) => <Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />);
DialogDescription.displayName = Description.displayName;
export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger };